import { Link } from 'react-router-dom';
import { useState, useEffect } from 'react';
import Card from '../../../components/base/Card';
import Button from '../../../components/base/Button';

interface Post {
  id: number;
  title: { rendered: string };
  slug: string;
  date: string;
  modified: string;
  author: number;
  categories: number[];
  _embedded?: any;
}

// Utility function to decode HTML entities
function decodeHtmlEntities(str: string) {
  const txt = document.createElement('textarea');
  txt.innerHTML = str;
  return txt.value;
}

export default function RecommendedArticles() {
  const [posts, setPosts] = useState<Post[]>(() => {
    const cached = localStorage.getItem('recommendedPosts');
    return cached ? JSON.parse(cached) : [];
  });

  const [loading, setLoading] = useState(posts.length === 0);
  const [avatars, setAvatars] = useState<Record<number, string>>({});
  const [categoriesMap, setCategoriesMap] = useState<Record<number, string>>({});

  useEffect(() => {
    const fetchRecommended = async () => {
      try {
        const res = await fetch(
          'https://blog.thecryptotutor.com/wp-json/wp/v2/posts?per_page=4&meta_key=recommended_article_switch&meta_value=true&_embed'
        );
        const data = await res.json();

        setPosts(data);
        localStorage.setItem('recommendedPosts', JSON.stringify(data));

        // Extract categories from all posts
        const categoryObj: Record<number, string> = {};
        data.forEach((post: Post) => {
          const termsGroups = post._embedded?.['wp:term'];
          if (!termsGroups) return;

          termsGroups.forEach((termGroup: any) => {
            termGroup.forEach((term: any) => {
              if (term.taxonomy === 'category') {
                categoryObj[term.id] = term.name;
              }
            });
          });
        });
        setCategoriesMap(categoryObj);

        // Fetch author avatars
        const avatarMap: Record<number, string> = {};
        await Promise.all(
          data.map(async (post: Post) => {
            const author = post._embedded?.author?.[0];
            if (!author) return;

            if (author.acf?.custom_avatar) {
              try {
                const mediaRes = await fetch(
                  `https://blog.thecryptotutor.com/wp-json/wp/v2/media/${author.acf.custom_avatar}`
                );
                const mediaData = await mediaRes.json();
                avatarMap[author.id] = mediaData.source_url || '/default-avatar.jpg';
              } catch {
                avatarMap[author.id] = '/default-avatar.jpg';
              }
            } else if (author.avatar_urls?.['96']) {
              avatarMap[author.id] = author.avatar_urls['96'];
            } else {
              avatarMap[author.id] = '/default-avatar.jpg';
            }
          })
        );

        setAvatars(avatarMap);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchRecommended();
  }, []);

  const skeletonCards = Array.from({ length: 4 }).map((_, i) => (
    <div key={i} className="animate-pulse flex flex-col h-80 bg-gray-200 rounded-lg" />
  ));

  return (
    <section className="py-5 bg-[#F5F7FA]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-white rounded-lg p-8 shadow-sm">
          <div className="flex justify-between items-center mb-8">
            <h2 className="text-3xl font-bold text-gray-900">Recommended Articles</h2>
            <Link to="/blog">
              <Button variant="outline" size="sm">
                See More <i className="ri-arrow-right-line ml-2"></i>
              </Button>
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {loading && posts.length === 0
              ? skeletonCards
              : posts.map((post) => {
                  const featuredMedia =
                    post._embedded?.['wp:featuredmedia']?.[0]?.source_url || '/default-image.jpg';

                  const authorData = post._embedded?.author?.[0];
                  const authorAvatar = authorData
                    ? avatars[authorData.id] || '/default-avatar.jpg'
                    : '/default-avatar.jpg';

                  const postDate = new Date(post.modified);
                  const formattedDate = postDate.toLocaleDateString('en-US', {
                    day: 'numeric',
                    month: 'short',
                    year: 'numeric',
                  });

                  const postCategories = post.categories
                    .map((catId) => categoriesMap[catId])
                    .filter(Boolean)
                    .join(', ');

                  return (
                    <Link key={post.id} to={`/blog/${post.slug}`} className="group h-full">
                      <Card hover className="flex flex-col h-full overflow-hidden">
                        <div className="aspect-video overflow-hidden flex-shrink-0">
                          <img
                            src={featuredMedia}
                            alt={decodeHtmlEntities(post.title.rendered)}
                            className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                          />
                        </div>

                        <div className="p-4 flex flex-col flex-1">
                          {/* Title */}
                          <h3 className="font-semibold text-gray-900 mb-3 line-clamp-2 leading-tight group-hover:text-blue-600 transition-colors">
                            {decodeHtmlEntities(post.title.rendered)}
                          </h3>

                          {/* Stick this entire block to bottom */}
                          <div className="mt-auto">
                            {/* Author + Date */}
                            <div className="flex items-center justify-between text-xs text-gray-500 space-x-2">
                              <div className="flex items-center space-x-2">
                                <img
                                  src={authorAvatar}
                                  alt={authorData?.name || 'Author'}
                                  className="w-5 h-5 rounded-lg object-cover"
                                />

                                {authorData && (
                                  <Link
                                    to={`/author/${authorData.slug}`}
                                    className="hover:text-blue-600 transition-colors"
                                    onClick={(e) => e.stopPropagation()}
                                  >
                                    {authorData.acf?.full_name || authorData.name}
                                  </Link>
                                )}
                              </div>

                              <span>{formattedDate}</span>
                            </div>

                            {/* Category */}
                            {postCategories && (
                              <div className="mt-2 text-xs text-blue-600 font-medium truncate">
                                {postCategories}
                              </div>
                            )}
                          </div>
                        </div>
                      </Card>
                    </Link>
                  );
                })}
          </div>
        </div>
      </div>
    </section>
  );
}
